import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Database, ArrowRight } from 'lucide-react';

/**
 * TenantBanner - Shows info when LIVE tenant has no deals
 * 
 * Props:
 * - dealCount: number of deals in current view (to detect empty live tenant)
 * - className: optional additional classes
 */
const TenantBanner = ({ dealCount = 0, loading = false, className = '' }) => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user } = useAuth();
  
  const isAdmin = user?.roles?.includes('admin');
  const isLiveEmpty = dealCount === 0;
  
  // Don't show the banner while deals are still loading (avoids a flash of
  // "No deals found" before data arrives), or once the tenant has deals.
  if (loading || !isLiveEmpty) {
    return null;
  }
  
  return (
    <Alert 
      className={`mb-6 border-blue-500 bg-blue-50 ${className}`}
      data-testid="tenant-banner"
    >
      <div className="flex items-start gap-3">
        <Database className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge 
              variant="outline" 
              className="border-blue-600 text-blue-700 bg-blue-100"
              data-testid="tenant-badge"
            >
              LIVE
            </Badge>
          </div>
          
          <AlertDescription className="text-blue-800">
            <strong>No deals found.</strong> Import deals via CSV Import or Push to LIVE.
            <span className="block mt-1 text-sm opacity-80">
              Use the buttons below to get started with data import.
            </span>
          </AlertDescription>
          
          {/* Admin-only quick links */}
          {isAdmin && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                className="border-blue-500 text-blue-700 hover:bg-blue-100"
                onClick={() => navigate('/daily-import-review')}
                data-testid="go-to-import"
              >
                <ArrowRight className="h-4 w-4 mr-1" />
                Import Data
              </Button>
            </div>
          )}
        </div>
      </div>
    </Alert>
  );
};

export default TenantBanner;
